import React from "react";

export const STATUS_CONFIG = {
  new: { label: "New", color: "bg-blue-100 text-blue-700" },
  under_review: { label: "Under Review", color: "bg-amber-100 text-amber-700" },
  accepted: { label: "Accepted", color: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700" },
};

export const DEAL_LABELS = { cash: "Cash Purchase", barter: "Barter (% of Apartments)", mixed: "Mixed (Cash + %)" };

export const emptyForm = {
  name: "", location: "", description: "",
  total_land_area_m2: "", gross_buildable_m2: "", deduction_percentage: "20",
  commercial_gross_m2: "", commercial_deduction_percentage: "20",
  deal_type: "cash", land_price: "", owner_percentage: "", cash_portion: "",
  communal_tax_per_m2: "", construction_cost_per_m2: "",
  residential_sale_price_per_m2: "", commercial_sale_price_per_m2: "",
  notes: "", status: "new",
};

export const NUM_FIELDS = [
  "total_land_area_m2","gross_buildable_m2","deduction_percentage",
  "commercial_gross_m2","commercial_deduction_percentage",
  "land_price","owner_percentage","cash_portion",
  "communal_tax_per_m2","construction_cost_per_m2",
  "residential_sale_price_per_m2","commercial_sale_price_per_m2"
];

export const fmt = (n) => new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 }).format(n || 0);
export const fmtEur = (n) => `€${fmt(n)}`;

export function MarkdownContent({ text }) {
  const lines = text.split("\n");
  const elements = [];
  let i = 0;

  const renderInline = (str) => {
    const parts = [];
    let remaining = str;
    let key = 0;
    while (remaining) {
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      if (boldMatch) {
        const idx = boldMatch.index;
        if (idx > 0) parts.push(<span key={key++}>{remaining.slice(0, idx)}</span>);
        parts.push(<strong key={key++} className="font-semibold text-slate-800">{boldMatch[1]}</strong>);
        remaining = remaining.slice(idx + boldMatch[0].length);
      } else {
        parts.push(<span key={key++}>{remaining}</span>);
        break;
      }
    }
    return parts;
  };

  while (i < lines.length) {
    const line = lines[i].trim();
    if (!line) { i++; continue; }

    if (line.startsWith("### ") || line.startsWith("## ") || line.startsWith("# ")) {
      const heading = line.replace(/^#+\s*/, "").replace(/\*+/g, "");
      elements.push(
        <div key={i} className="flex items-center gap-2 mt-4 mb-2 first:mt-0">
          <div className="w-1 h-5 rounded-full bg-violet-400" />
          <h4 className="text-sm font-bold text-violet-800">{heading}</h4>
        </div>
      );
    } else if (line.startsWith("- ") || line.startsWith("* ")) {
      const items = [];
      while (i < lines.length) {
        const l = lines[i].trim();
        if (l.startsWith("- ") || l.startsWith("* ")) {
          items.push(l.slice(2));
          i++;
        } else if (l.startsWith("  - ") || l.startsWith("  * ")) {
          items.push("  " + l.trim().slice(2));
          i++;
        } else break;
      }
      elements.push(
        <ul key={`ul-${i}`} className="space-y-1.5 mb-2">
          {items.map((item, j) => {
            const isNested = item.startsWith("  ");
            const text = isNested ? item.trim() : item;
            return (
              <li key={j} className={`flex gap-2 text-sm text-slate-600 leading-relaxed ${isNested ? "ml-4" : ""}`}>
                <span className={`mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0 ${isNested ? "bg-slate-300" : "bg-violet-300"}`} />
                <span>{renderInline(text)}</span>
              </li>
            );
          })}
        </ul>
      );
      continue;
    } else {
      elements.push(<p key={i} className="text-sm text-slate-600 leading-relaxed mb-2">{renderInline(line)}</p>);
    }
    i++;
  }
  return <div>{elements}</div>;
}

export function computeLocal(f) {
  const totalGross = parseFloat(f.gross_buildable_m2) || 0;
  const commGross = parseFloat(f.commercial_gross_m2) || 0;
  const resGross = Math.max(0, totalGross - commGross);

  const resDed = parseFloat(f.deduction_percentage) || 0;
  const resNet = resGross * (1 - resDed / 100);

  const commDed = parseFloat(f.commercial_deduction_percentage) || 0;
  const commNet = commGross * (1 - commDed / 100);

  const totalNet = resNet + commNet;

  const ownerPct = parseFloat(f.owner_percentage) || 0;
  const isBarter = f.deal_type === "barter" || f.deal_type === "mixed";
  const ownerResM2 = isBarter ? resNet * (ownerPct / 100) : 0;
  const ownerCommM2 = isBarter ? commNet * (ownerPct / 100) : 0;
  const ownerTotal = ownerResM2 + ownerCommM2;

  const yourRes = resNet - ownerResM2;
  const yourComm = commNet - ownerCommM2;
  const yourTotal = yourRes + yourComm;

  const commTax = totalNet * (parseFloat(f.communal_tax_per_m2) || 0);
  // Construction cost covers the FULL net area — in barter you still build everything
  // (the owner's portion is how you pay for the land, not a cost reduction)
  const constr = totalNet * (parseFloat(f.construction_cost_per_m2) || 0);
  const landCost = f.deal_type !== "barter" ? (parseFloat(f.land_price) || 0) : 0;
  const cashPortion = f.deal_type === "mixed" ? (parseFloat(f.cash_portion) || 0) : 0;
  const totalCosts = landCost + cashPortion + commTax + constr;

  const resPrice = parseFloat(f.residential_sale_price_per_m2) || 0;
  const commPrice = parseFloat(f.commercial_sale_price_per_m2) || 0;
  const resRevenue = yourRes * resPrice;
  const commRevenue = yourComm * commPrice;
  const totalRevenue = resRevenue + commRevenue;
  const profit = totalRevenue - totalCosts;
  const margin = totalRevenue > 0 ? (profit / totalRevenue * 100) : 0;

  const barterResValue = ownerResM2 * resPrice;
  const barterCommValue = ownerCommM2 * commPrice;
  const barterTotal = barterResValue + barterCommValue;

  const enteredPct = Math.round(parseFloat(f.owner_percentage) || 30);
  const scenarioPcts = Array.from({ length: 6 }, (_, i) => enteredPct - 1 - i);
  const barterScenarios = scenarioPcts.map(pct => {
    const oRes = resNet * (pct / 100);
    const oComm = commNet * (pct / 100);
    const cashEquiv = oRes * resPrice + oComm * commPrice;
    return { pct, ownerRes: oRes, ownerComm: oComm, cashEquiv };
  });

  return {
    totalGross, resGross, resNet, commGross, commNet, totalNet,
    ownerResM2, ownerCommM2, ownerTotal,
    yourRes, yourComm, yourTotal,
    commTax, constr, landCost, cashPortion,
    totalCosts, resRevenue, commRevenue, totalRevenue, profit, margin,
    barterResValue, barterCommValue, barterTotal,
    barterScenarios,
  };
}
